import React from 'react';
import type { ModuleDefinition } from './moduleRegistry';
import type { MonthCell, ReportData } from '../data/types';
import { ScreenSection, ScreenMonthGrid, ScreenDivider, ScreenStatusBar, ScreenDelayBars } from '../primitives/ScreenPrimitives';
import { colors, coverFonts, overviewColors, ptToPx, space } from '../data/designTokens';

const STATUS_PALETTE: Record<MonthCell['status'], { label: string; color: string }> = {
    on_time: { label: 'On time', color: colors.pos },
    delayed: { label: 'Delayed', color: colors.warn },
    default: { label: 'Default', color: colors.neg },
    missing: { label: 'Missing', color: colors.neutralSoft },
};

const countBy = (cells: MonthCell[]): Array<{ label: string; count: number; color: string }> => {
    const buckets: Record<MonthCell['status'], number> = { on_time: 0, delayed: 0, default: 0, missing: 0 };
    cells.forEach((c) => {
        buckets[c.status] += 1;
    });
    return (['on_time', 'delayed', 'default', 'missing'] as const).map((k) => ({
        label: STATUS_PALETTE[k].label,
        count: buckets[k],
        color: STATUS_PALETTE[k].color,
    }));
};

// ── Headline card (Figma node 32:100072) ──────────────────────────────
//
// Three side-by-side cards: GSTR-1, GSTR-3B, EPF on-time filing %. Tone is
// derived from the percentage so the eye reads it instantly:
//   ≥ 95%  → green   (excellent)
//   ≥ 80%  → orange  (warn)
//   < 80%  → red     (fail)

const SF = coverFonts.screen;

type ComplianceTone = 'pos' | 'warn' | 'neg' | 'neutral';

const TONE_INK: Record<ComplianceTone, string> = {
    pos: colors.pos,
    warn: colors.warn,
    neg: colors.neg,
    neutral: overviewColors.inkMuted,
};

const headlineTone = (value?: string): ComplianceTone => {
    if (!value) return 'neutral';
    const m = value.match(/(\d+(?:\.\d+)?)/);
    if (!m) return 'neutral';
    const pct = parseFloat(m[1]);
    if (!Number.isFinite(pct)) return 'neutral';
    if (pct >= 95) return 'pos';
    if (pct >= 80) return 'warn';
    return 'neg';
};

const headlineDesign = {
    gridGap: 12,
    cardPad: 11,
    cardRadius: 4,
    cardBorder: 0.5,
    labelSize: 7,
    labelTrackingEm: 0.08,
    labelGap: 5,
    // Percentage value (display font, color-coded). 16pt reads as the "headline"
    // without dominating the card — at 22pt a long string like "No EPF filing data
    // available" wrapped to three lines and broke the row.
    valueSize: 16,
    valueLineHeight: 1.1,
    // Fallback value (smaller, neutral) — used when the API returns a message
    // like "No EPF filing data available" instead of a "%" reading.
    fallbackValueSize: 8,
    fallbackValueLineHeight: 1.4,
    durationSize: 7,
    durationGap: 5,
    // Constant card height — keeps the three cards visually aligned even when
    // one returns a message and the others a clean percentage.
    minHeight: 64,
} as const;

// "100%" / "94%" / "85.5%" → percentage card. Anything else (e.g. "No EPF data
// available", undefined) → fallback card.
const isPercentageValue = (v?: string): boolean => {
    if (!v) return false;
    return /\d+(?:\.\d+)?\s*%/.test(v);
};

const ScreenComplianceCard: React.FC<{ label: string; value?: string; duration?: string }> = ({ label, value, duration }) => {
    const isPct = isPercentageValue(value);
    const tone = isPct ? headlineTone(value) : 'neutral';
    const ink = TONE_INK[tone];
    return (
        <div
            style={{
                flex: 1,
                border: `${ptToPx(headlineDesign.cardBorder)}px solid ${overviewColors.border}`,
                borderLeft: `2px solid ${tone === 'neutral' ? overviewColors.border : ink}`,
                borderRadius: ptToPx(headlineDesign.cardRadius),
                padding: ptToPx(headlineDesign.cardPad),
                background: '#FFFFFF',
                minHeight: ptToPx(headlineDesign.minHeight),
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                style={{
                    fontSize: ptToPx(headlineDesign.labelSize),
                    fontFamily: SF.sans,
                    fontWeight: 600,
                    // Card label stays ink — only the numeric value picks up the tone colour.
                    color: overviewColors.ink,
                    letterSpacing: `${headlineDesign.labelTrackingEm}em`,
                    textTransform: 'uppercase',
                    marginBottom: ptToPx(headlineDesign.labelGap),
                }}
            >
                {label}
            </div>
            {isPct ? (
                <div
                    style={{
                        fontSize: ptToPx(headlineDesign.valueSize),
                        fontFamily: SF.display,
                        fontWeight: 500,
                        color: ink,
                        lineHeight: headlineDesign.valueLineHeight,
                    }}
                >
                    {value}
                </div>
            ) : (
                <div
                    style={{
                        fontSize: ptToPx(headlineDesign.fallbackValueSize),
                        fontFamily: SF.sans,
                        fontWeight: 500,
                        color: overviewColors.inkMuted,
                        lineHeight: headlineDesign.fallbackValueLineHeight,
                    }}
                >
                    {value ?? '—'}
                </div>
            )}
            {duration ? (
                <div
                    style={{
                        fontSize: ptToPx(headlineDesign.durationSize),
                        fontFamily: SF.mono,
                        color: overviewColors.inkMuted,
                        marginTop: ptToPx(headlineDesign.durationGap),
                    }}
                >
                    {duration}
                </div>
            ) : null}
        </div>
    );
};

const ScreenComplianceCardRow: React.FC<{ items: Array<{ label: string; value?: string; duration?: string }> }> = ({ items }) => (
    <div style={{ display: 'flex', gap: ptToPx(headlineDesign.gridGap) }}>
        {items.map((it, i) => (
            <ScreenComplianceCard key={i} label={it.label} value={it.value} duration={it.duration} />
        ))}
    </div>
);

const ScreenCompliances: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const c = data.compliances;
    if (!c) return null;
    const groups: Array<{ title: string; primary?: string; cells: MonthCell[] }> = [
        { title: 'GSTR-1 (outward supplies)', primary: c.gstr1Pct, cells: c.gstr1 },
        { title: 'GSTR-3B (summary)', primary: c.gstr3bPct, cells: c.gstr3b },
        { title: 'EPF filings', primary: c.epfPct, cells: c.epf },
    ].filter((g) => g.cells.length > 0);

    return (
        <ScreenSection kicker="Compliances" title="Statutory Compliance" index={index}>
            {c.headlineCards?.length ? (
                <ScreenComplianceCardRow
                    items={c.headlineCards.slice(0, 3).map((h) => ({
                        label: h.title,
                        value: h.value,
                        duration: h.duration,
                    }))}
                />
            ) : null}
            {groups.map((g, i) => (
                <div key={g.title} style={{ marginTop: i === 0 ? 0 : ptToPx(space.sp8) }}>
                    {/* Mirror PDF rhythm — sp6/sp8 in pt, scaled to px. Keeps the
                        screen preview visually aligned with the exported PDF. */}
                    {i === 0 ? <ScreenDivider /> : null}
                    <ScreenMonthGrid title={g.title} primary={g.primary} secondary="on time" cells={g.cells} />
                    <div style={{ marginTop: ptToPx(space.sp6) }}>
                        <ScreenStatusBar segments={countBy(g.cells)} />
                    </div>
                    <div style={{ marginTop: ptToPx(space.sp6) }}>
                        <ScreenDelayBars cells={g.cells} />
                    </div>
                    {i < groups.length - 1 ? <ScreenDivider /> : null}
                </div>
            ))}
        </ScreenSection>
    );
};

export const compliancesModule: ModuleDefinition = {
    meta: {
        id: 'compliances',
        title: 'Statutory Compliance',
        description: 'GSTR-1, GSTR-3B & EPF filing punctuality with on-time scoring.',
        dataSource: 'GSTR-1, GSTR-3B, EPF, Compliances Headline',
    },
    hasData: (d) => {
        const c = d.compliances;
        if (!c) return false;
        return c.gstr1.length > 0 || c.gstr3b.length > 0 || c.epf.length > 0 || (c.headlineCards?.length ?? 0) > 0;
    },
    Screen: ScreenCompliances,
};
